import {omit, pick, isNil} from 'lodash'
import {KEYFRAME_PROPS} from 'powtoon-document/consts/animationGraphs'
import {getRectangleCenterPoint, alignItemByCenterPoint} from 'powtoon-commons/utils'
import {SLIDE_WIDTH, SLIDE_HEIGHT} from 'powtoon-commons/models/consts'
import cloneTrack from 'powtoon-commons/models/utils/cloneTrack'
// import {createLogger} from 'logging'
import {roundHundred, roundDecimalInteger} from 'powtoon-commons/utils/numbers'
import {hasAnimationGraphs} from 'powtoon-commons/utils'
import {ENTER_EFFECT_IDS} from 'powtoon-document/consts'
import {isCameraItem} from 'powtoon-document'
import {
  DEFAULT_ANIMATION_DURATION,
  DEFAULT_B_POSITIONS_OFFSET,
  MIN_ANIMATION_DURATION
} from 'powtoon-html-studio/src/timeline/consts/index.ts'

// const logger = createLogger('models', 'trackAnimationGraphs')

function getDefaulteKeyframesEndTime(trackStartTime, trackEndTime) {
  return ((trackEndTime - trackStartTime) > DEFAULT_ANIMATION_DURATION) ?
    (trackStartTime + DEFAULT_ANIMATION_DURATION) :
    trackEndTime
}

export function calculateDefaultKeyframeValues(sprite, keyframeIndex) {
  const keyframeValues = pick(sprite, KEYFRAME_PROPS)

  if (keyframeIndex === 0) {
    return keyframeValues
  }

  if (isCameraItem(sprite) && (
    keyframeValues.width + (2 * DEFAULT_B_POSITIONS_OFFSET) > SLIDE_WIDTH ||
    keyframeValues.height + (2 * DEFAULT_B_POSITIONS_OFFSET) > SLIDE_HEIGHT
  )) {
    keyframeValues.width = Math.round(keyframeValues.width / 2)
    keyframeValues.height = Math.round(keyframeValues.height / 2)
  }

  const spriteCenterPoint = getRectangleCenterPoint(keyframeValues)

  keyframeValues.x += (spriteCenterPoint.x <= (SLIDE_WIDTH / 2)) ?
    DEFAULT_B_POSITIONS_OFFSET :
    -DEFAULT_B_POSITIONS_OFFSET
  keyframeValues.y += (spriteCenterPoint.y <= (SLIDE_HEIGHT / 2)) ?
    DEFAULT_B_POSITIONS_OFFSET :
    -DEFAULT_B_POSITIONS_OFFSET

  return keyframeValues
}

export function removeTrackAnimationGraphs(track) {
  return omit(track, 'animationGraphs')
}

export function createDefaultAnimationGraphsTrackUpdateInfo(track) {
  const {sprites: [sprite], startTime, endTime} = track

  return {
    animationGraphs: {
      keyframes: [
        {
          time: startTime,
          values: calculateDefaultKeyframeValues(sprite, 0)
        },
        {
          time: getDefaulteKeyframesEndTime(startTime, endTime),
          values: calculateDefaultKeyframeValues(sprite, 1)
        }
      ]
    }
  }
}

export function createDefaultAnimationGraphsCameraUpdateInfo({sprites: [sprite]}, currentTime, slideDuration) {
  const endTime = roundDecimalInteger(getDefaulteKeyframesEndTime(currentTime, slideDuration))
  const startTime = roundDecimalInteger(Math.min(endTime - MIN_ANIMATION_DURATION, currentTime))
  return {
    animationGraphs: {
      keyframes: [
        {
          time: startTime,
          values: calculateDefaultKeyframeValues(sprite, 0)
        },
        {
          time: getDefaulteKeyframesEndTime(startTime, endTime),
          values: calculateDefaultKeyframeValues(sprite, 1)
        }
      ]
    }
  }
}

export function updateAnimationGraphsKeyframesTime(
  animationGraphs,
  newTrackStartTime,
  newTrackEndTime,
  newAnimKeyframeTimes = null,
  track = null
) {

  if (!animationGraphs) {return undefined}
  if (!newAnimKeyframeTimes && !track) {return animationGraphs}

  if (track) {
    let newAnimStartTime = roundDecimalInteger(animationGraphs.keyframes[0].time)
    let newAnimEndTime = roundDecimalInteger(animationGraphs.keyframes[1].time)

    const trackDuration = track.endTime - track.startTime
    const newTrackDuration = newTrackEndTime - newTrackStartTime
    const isSameTrackDuration = trackDuration === newTrackDuration

    if (isSameTrackDuration) {
      const deltaTrackStartTime = track.startTime - newTrackStartTime
      const relativeAnimStartTime = newAnimStartTime - deltaTrackStartTime
      const relativeAnimEndTime = newAnimEndTime - deltaTrackStartTime

      return {
        ...animationGraphs,
        keyframes: [
          {...animationGraphs.keyframes[0], time: relativeAnimStartTime},
          {...animationGraphs.keyframes[1], time: relativeAnimEndTime}
        ]
      }
    }

    const deltaTrackStartTime = Math.max(newTrackStartTime - newAnimStartTime, 0)
    const deltaTrackEndTime = Math.max(newAnimEndTime - newTrackEndTime, 0)
    const addMinDuration = newTrackEndTime - newTrackStartTime < MIN_ANIMATION_DURATION
      ? MIN_ANIMATION_DURATION
      : 0

    if (newTrackStartTime > newAnimStartTime) {
      if (newTrackEndTime < (newAnimEndTime + deltaTrackStartTime)) {
        newAnimStartTime = newTrackStartTime - addMinDuration
      } else {
        newAnimStartTime = Math.max(newAnimStartTime, newTrackStartTime)
      }
      newAnimEndTime = Math.min(newAnimEndTime + deltaTrackStartTime, newTrackEndTime)
    }
    if (newTrackEndTime < newAnimEndTime) {
      if (newTrackStartTime > (newAnimStartTime - deltaTrackEndTime)) {
        newAnimEndTime = newTrackEndTime + addMinDuration
      } else {
        newAnimEndTime = Math.min(newAnimEndTime, newTrackEndTime)
      }
      newAnimStartTime = Math.max(newAnimStartTime - deltaTrackEndTime, newTrackStartTime)
    }

    return {
      ...animationGraphs,
      keyframes: [
        {...animationGraphs.keyframes[0], time: newAnimStartTime},
        {...animationGraphs.keyframes[1], time: newAnimEndTime}
      ]
    }
  }

  const deltaAnimStartTime = Math.max(newAnimKeyframeTimes[0], newTrackStartTime)
  const deltaAnimEndTime = Math.min(newAnimKeyframeTimes[1], newTrackEndTime)

  return {
    ...animationGraphs,
    keyframes: [
      {...animationGraphs.keyframes[0], time: deltaAnimStartTime},
      {...animationGraphs.keyframes[1], time: deltaAnimEndTime}
    ]
  }
}

export function getTrackKeyframes(track) {

  if (!hasAnimationGraphs(track)) {
    // logger.error(new Error('Track should have animationGraphs data'), track)
    return
  }

  return track.animationGraphs.keyframes
}

export function getTrackKeyframeByIndex(track, keyframeIndex) {
  const keyframe = getTrackKeyframes(track)[keyframeIndex]

  if (isNil(keyframe)) {
    // logger.error(new Error('Track does not have keyframe with index'), {track, keyframeIndex})
    return
  }

  return keyframe
}

export function getTrackKeyframeTime(track, keyframeIndex) {
  return getTrackKeyframeByIndex(track, keyframeIndex).time
}

export function getTrackKeyframeValues(track, keyframeIndex) {
  return getTrackKeyframeByIndex(track, keyframeIndex).values
}

export function getTrackKeyframeTimes(track) {
  const keyframes = getTrackKeyframes(track)
  const startAbsoluteTime = keyframes[0].time
  const endAbsoluteTime = keyframes[1].time
  return [startAbsoluteTime, endAbsoluteTime]
}

export function updateAnimationGraphsKeyframeValues(animationGraphs, propsUpdateData, keyframeIndex = null) {
  return {
    ...animationGraphs,
    keyframes: animationGraphs.keyframes.map((keyframe, index) => {

      if (keyframeIndex !== null && index !== keyframeIndex) {
        return keyframe
      }

      const keyframeValues = keyframe.values

      const propsForUpdate = typeof propsUpdateData === 'function' ?
        propsUpdateData(keyframeValues, index) :
        propsUpdateData

      return {
        ...keyframe,
        values: {
          ...keyframeValues,
          ...pick(propsForUpdate, KEYFRAME_PROPS)
        }
      }
    })
  }
}

export function fitTextScaledToHeightExistingTrackKeyframes(animationGraphs, correctSize) {
  const propsUpdateData = (prevKeyframeValues, index) => {
    if (index === 0) {
      return {...pick(correctSize, KEYFRAME_PROPS), rotation: prevKeyframeValues.rotation}
    }
    return {...prevKeyframeValues, height: correctSize.height}
  }
  return updateAnimationGraphsKeyframeValues(animationGraphs, propsUpdateData)
}

export function fitBoxToExistingTrackKeyframes(animationGraphs, newBox) {
  const firstKeyframeValues = animationGraphs.keyframes[0].values
  const sizeCoefficients = {
    width: newBox.width / firstKeyframeValues.width,
    height: newBox.height / firstKeyframeValues.height
  }

  const propsUpdateData = (prevKeyframeValues, index) => {
    if (index === 0) {
      return {...pick(newBox, KEYFRAME_PROPS), rotation: prevKeyframeValues.rotation}
    }
    let newKeyframeValues = {
      width: prevKeyframeValues.width * sizeCoefficients.width,
      height: prevKeyframeValues.height * sizeCoefficients.height
    }
    newKeyframeValues = alignItemByCenterPoint(newKeyframeValues, prevKeyframeValues)
    return {...newKeyframeValues, rotation: prevKeyframeValues.rotation}
  }
  return updateAnimationGraphsKeyframeValues(animationGraphs, propsUpdateData)
}

export function createNewTrackFromKeyframe(track, keyframeIndex,
                                           {
                                             leaveAnimationGraphs = false,
                                             resetEnterEffectId = false,
                                             trackId = null
                                           } = {}) {

  if (!hasAnimationGraphs(track)) {
    // logger.error(new Error('Track should have animationGraphs data'), track)
    return
  }

  const {values} = getTrackKeyframeByIndex(track, keyframeIndex)

  const trackUpdateInfo = {
    updateSpriteData: values,
    extraTrackData: {}
  }

  if (keyframeIndex === 1){
    trackUpdateInfo.extraTrackData.startTime = track.endTime
  }

  if (trackId !== null) {
    trackUpdateInfo.extraTrackData.id = trackId
  }

  if (resetEnterEffectId){
    track.enterEffect.effectId = ENTER_EFFECT_IDS.NO_EFFECT
  }

  return cloneTrack(leaveAnimationGraphs ? track : removeTrackAnimationGraphs(track), trackUpdateInfo)
}

export function getTimeLineDragBoundaries(
  slideDuration = 0,
  durationUnitWidth = 0,
  startTime = 0,
  endTime = 0,
  maxTimeDuration,
  minTimeDuration
){
  const timeDiff = endTime - startTime
  const selectionWidth = roundHundred(durationUnitWidth * timeDiff)

  const left = roundHundred(durationUnitWidth * startTime)
  const right = roundHundred(left + selectionWidth)

  const maxRight = durationUnitWidth * slideDuration
  const maxLeft = roundHundred(maxRight - selectionWidth)
  const useAdditionalParametersSent = maxTimeDuration !== undefined && minTimeDuration !== undefined

  if (useAdditionalParametersSent) {
    const maxDurationWidth = durationUnitWidth * maxTimeDuration
    const minDurationWidth = durationUnitWidth * minTimeDuration
    return {
      left,
      right,
      maxRight: Math.min(roundHundred(right + maxDurationWidth), maxRight),
      minLeft: Math.max( roundHundred(left - minDurationWidth), 0),
      maxLeft
    }
  }
  else {
    return {
      left,
      right,
      maxRight: durationUnitWidth * slideDuration,
      minLeft: 0,
      maxLeft
    }
  }
}
